import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Pagination } from "../../components/compIndex";

const verified = "https://pixelore.s3.eu-central-003.backblazeb2.com/Random+Images/verified.png";
const suspicious = "https://pixelore.s3.eu-central-003.backblazeb2.com/Random+Images/suspicious.png";

const GuildLookup = () => {
  const [guildData, setGuildData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [guildsPerPage] = useState(21);
  const [searchQuery, setSearchQuery] = useState("");
  const [officialOnly, setOfficialOnly] = useState(false);
  const [suspiciousOnly, setSuspiciousOnly] = useState(false);
  const [minFeeFilter, setMinFeeFilter] = useState(null);
  const [maxFeeFilter, setMaxFeeFilter] = useState(null);
  const [mapCountFilter, setMapCountFilter] = useState(0);
  const [whitelistState, setWhitelistFilter] = useState(0); //0: show all, 1: show only whitelist, 2: show no whitelist
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const guildResponse = await axios.get("https://api.pixelore.wiki/api/guilds");
        setGuildData(guildResponse.data);
      } catch (error) {
        console.log("Issue fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  const toggleExpand = (id) => {
    setIsExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleOfficialChange = (e) => {
    setOfficialOnly(e.target.checked);
  };

  const handleSuspiciousChange = (e) => {
    setSuspiciousOnly(e.target.checked);
  };

  const handleMinFeeFilterChange = (e) => {
    setMinFeeFilter(e.target.value ? parseInt(e.target.value) : null);
  };

  const handleMaxFeeFilterChange = (e) => {
    setMaxFeeFilter(e.target.value ? parseInt(e.target.value) : null);
  };

  const handleMapCountFilterChange = (e) => {
    setMapCountFilter(parseInt(e.target.value));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleWhitelistFilterState = () => setWhitelistFilter((prev) => (prev + 1) % 3); // Cycles through 0, 1, 2

  const handleClearFilters = () => {
    setSearchQuery("");
    setOfficialOnly(false);
    setSuspiciousOnly(false);
    setMinFeeFilter(null);
    setMaxFeeFilter(null);
    setMapCountFilter(0);
    setWhitelistFilter(0);
  };

  const filteredGuilds = guildData.filter((guild) => {
    const matchesSearchQuery =
      guild.name.toLowerCase().includes(searchQuery) || guild.handle.toLowerCase().includes(searchQuery) || guild.id.toString().includes(searchQuery);

    const matchesOfficial = officialOnly ? guild.official : true;
    const matchesSuspicious = suspiciousOnly ? guild.suspicious : true;
    const matchesMinFee = minFeeFilter !== null ? guild.currentFee >= minFeeFilter : true;
    const matchesMaxFee = maxFeeFilter !== null ? guild.currentFee <= maxFeeFilter : true;
    const matchesMapCount = guild.mapCount >= mapCountFilter;
    const matchesWhitelistState = whitelistState === 0 ? true : whitelistState === 1 ? guild.useWhitelist : !guild.useWhitelist;

    return matchesSearchQuery && matchesOfficial && matchesMinFee && matchesMaxFee && matchesMapCount && matchesSuspicious && matchesWhitelistState;
  });

  // Sort the filtered guilds
  const sortedGuilds = filteredGuilds.sort((a, b) => b.pledges - a.pledges);

  // Calculate pagination
  const indexOfLastGuild = currentPage * guildsPerPage;
  const indexOfFirstGuild = indexOfLastGuild - guildsPerPage;
  const currentGuilds = sortedGuilds.slice(indexOfFirstGuild, indexOfLastGuild);

  const totalPages = Math.ceil(filteredGuilds.length / guildsPerPage);
  const maxPageNumbers = 5;
  const halfPageRange = Math.floor(maxPageNumbers / 2);

  let startPage = Math.max(1, currentPage - halfPageRange);
  let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);

  if (endPage - startPage + 1 < maxPageNumbers) {
    startPage = Math.max(1, endPage - maxPageNumbers + 1);
  }

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="panel-style">
      <h1 className="panel-heading">Guilds</h1>
      {/*Filters*/}
      <div className="p-2 m-1 rounded-md text-sm border text-[var(--text-color)] filters-panel">
        {/* Search Input - Full width on mobile, half width on desktop */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Search by Name or Handle"
            value={searchQuery}
            onChange={handleSearch}
            className="p-2 select-forms w-full md:w-1/2"
          />
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Checkboxes - Stacked on mobile, inline on desktop */}
          <div className="flex flex-col md:flex-row md:gap-4 gap-2">
            <label className="flex items-center text-sm">
              <input type="checkbox" checked={officialOnly} onChange={handleOfficialChange} className="mr-2" />
              Official Guilds
            </label>

            <label className="flex items-center text-sm">
              <input type="checkbox" checked={suspiciousOnly} onChange={handleSuspiciousChange} className="mr-2" />
              Suspicious Guilds
            </label>
          </div>

          {/* Fee Filters - Stacked on mobile, inline on desktop */}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span className="text-xs">Fees:</span>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Min"
                value={minFeeFilter || ""}
                onChange={handleMinFeeFilterChange}
                className="p-1 rounded border border-gray-400 w-16 text-xs text-gray-500"
              />
              <input
                type="text"
                placeholder="Max"
                value={maxFeeFilter || ""}
                onChange={handleMaxFeeFilterChange}
                className="p-1 rounded border border-gray-400 w-16 text-xs text-gray-500"
              />
            </div>
          </div>

          {/* Lands Slider - Full width on mobile, auto width on desktop */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <span className="text-xs">Lands:</span>
            <div className="flex items-center gap-2 w-full">
              <input type="range" min="0" max="100" value={mapCountFilter} onChange={handleMapCountFilterChange} className="slider flex-1" />
              <span className="text-xs w-8 text-center">{mapCountFilter}</span>
            </div>
          </div>

          {/* Whitelist Button - Full width on mobile, auto width on desktop */}
          <button
            onClick={handleWhitelistFilterState}
            className="w-full md:w-36 panel-button"
          >
            {whitelistState === 0 && (
              <>
                <i className="fa-solid fa-circle-dot fa-xs" style={{ color: "#c2a8c1" }} /> All Whitelist
              </>
            )}
            {whitelistState === 1 && (
              <>
                <i className="fa-regular fa-circle-dot fa-xs" style={{ color: "#c2a8c1" }} /> Need Whitelist
              </>
            )}
            {whitelistState === 2 && (
              <>
                <i className="fa-solid fa-circle-notch fa-xs" style={{ color: "#c2a8c1" }} /> No Whitelist
              </>
            )}
          </button>

          {/* Clear Button - Full width on mobile, auto width on desktop */}
          <button
            onClick={handleClearFilters}
            className="w-full md:w-auto panel-button"
          >
            Clear
          </button>
        </div>

        {/* Results Count */}
        <p className="text-xs text-gray-400 mt-2">({filteredGuilds.length} Guilds Filtered)</p>
      </div>

      {/* Guilds Section */}
      <div className="grid lg:grid-cols-2 grid-cols-1 xl:grid-cols-3 guilds-list max-h-full text-sm">
        {currentGuilds.map((guild) => (
          <div key={guild.id} className={`relative flex flex-col component-style ${isExpanded[guild.id] ? "h-auto" : "h-60 overflow-hidden"}`}>
            {/* Suspicious Guild Icon */}
            {guild.suspicious && <img src={suspicious} alt="Suspicious" className="absolute h-10 w-10 top-0 right-[5px]" />}

            {/* Official Guild Icon */}
            {guild.official && <img src={verified} alt="Verified" className="absolute h-10 w-10 top-0 right-[5px]" />}

            <div className="guild-emblem flex flex-row mx-left">
              <img src={guild.emblem} alt={`${guild.name} Emblem`} className="w-16 h-16 md:w-16 md:h-16 object-cover mr-2 rounded-md" />
              <div>
                <h2 className="text-xl font-semibold font-teko text-retroegg">{guild.name}</h2>
                <div className="flex flex-row justify-center lg:justify-start">
                  <span>
                    <a
                      href={`https://dashboard.pixels.xyz/guild/${guild.handle}`}
                      rel="noreferrer"
                      target="_blank"
                      className="px-1 w-full lg:w-10 m-1 font-body border border-dashed text-sm border-white rounded-lg hover:bg-[var(--button-bg-color)]"
                    >
                      Pixels Guild Info
                    </a>
                    <br />
                  </span>
                  <span>
                  <Link
                      to={`/lookup/guilds/${guild.handle}`} target="_blank" rel="noreferrer"
                      className="px-1 w-full lg:w-10 m-1 font-body border border-dashed text-sm border-white rounded-lg hover:bg-[var(--button-bg-color)]">
                      Guild Info
                    </Link>
                  </span>
                </div>
                <div className="mt-2 space-x-2">
                  <span className="tags">
                    <i className="fa-solid fa-mountain-sun mr-1 text-xs" /> {guild.mapCount || 0}{" "}
                  </span>
                  <span className="tags">
                    <i className="fa-solid fa-user mr-1 text-xs" />
                    {guild.pledges || 0} / {guild.members || 0}
                  </span>
                  <span className="tags">
                    <i className="fa-solid fa-gem mr-1 text-xs" />
                    {guild.currentFee}
                  </span>
                  {guild.useWhitelist && (
                    <span className="tags">
                      <i className="fa-solid fa-w text-xs" />
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="guild-stats mt-2 flex flex-col md:flex-row gap-2 md:gap-5">
              {guild.owner && guild.owner[0] && guild.owner[0].username && (
                <span>
                  <strong>Owner:</strong>{" "}
                  <Link to={`/lookup/players/${guild.owner[0].username}`} target="_blank" rel="noreferrer" className="font-semibold">
                    {guild.owner[0].username}
                  </Link>
                </span>
              )}
            </div>
            <div className="guild-details w-full text-center md:text-left">
              <p className="mt-1">
                <strong>Description:</strong>{" "}
                {guild.description.length > 200 && !isExpanded[guild.id] ? `${guild.description.slice(0, 200)}...` : guild.description}
                {guild.description.length > 200 && (
                  <button onClick={() => toggleExpand(guild.id)} className="text-[var(--link)] text-xs ml-2 hover:text-[var(--hover-link]">
                    {isExpanded[guild.id] ? "See Less" : "See More"}
                  </button>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
      <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} startPage={startPage} endPage={endPage} />
    </div>
  );
};

export default GuildLookup;
